import { Alert, Button, Checkbox, FormControlLabel, Link, Stack, Step, StepLabel, Stepper, TextField } from "@mui/material";
import { useState, type ChangeEvent, type FormEvent } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";

import { AuthLayout } from "../accounts_login_ui/AuthLayout";
import { api, ensureCsrf, errorMessage, setActiveOrganization } from "../api/client";
import { useAuth } from "../auth/AuthProvider";

const STEPS = ["Your account", "Your company"];

export function Signup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "", company_name: "", accept_terms: false, website: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, base_currency: "USD", plan: params.get("plan") ?? "",
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (step === 0) { setStep(1); return; }
    setBusy(true);
    setError(null);
    await ensureCsrf();
    const { data, error: err } = await api.POST("/api/v1/accounts/auth/signup/", { body: form });
    setBusy(false);
    if (err || !data) { setError(errorMessage(err)); return; }
    setActiveOrganization(data.organization);
    await refresh();
    navigate("/app/onboarding", { replace: true });
  }

  return (
    <AuthLayout title="Start your free trial" subtitle="14 days, no credit card. Set up in minutes." width={460}
      footer={<>Already have an account? <Link component={RouterLink} to="/login">Sign in</Link></>}>
      <Stepper activeStep={step} sx={{ mb: 3 }}>{STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}</Stepper>
      <form onSubmit={onSubmit}>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {step === 0 ? (
            <>
              <Stack direction="row" spacing={2}>
                <TextField label="First name" value={form.first_name} onChange={set("first_name")} required autoFocus />
                <TextField label="Last name" value={form.last_name} onChange={set("last_name")} />
              </Stack>
              <TextField label="Work email" type="email" value={form.email} onChange={set("email")} required autoComplete="email" />
              <TextField label="Password" type="password" value={form.password} onChange={set("password")} required autoComplete="new-password" helperText="At least 10 characters." />
              <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" style={{ position: "absolute", left: -9999 }} aria-hidden />
            </>
          ) : (
            <>
              <TextField label="Company name" value={form.company_name} onChange={set("company_name")} required autoFocus />
              <TextField label="Timezone" value={form.timezone} onChange={set("timezone")} helperText="Used for schedules and due dates." />
              <FormControlLabel control={<Checkbox checked={form.accept_terms} onChange={set("accept_terms")} required />}
                label={<>I agree to the <Link component={RouterLink} to="/terms" target="_blank">Terms of Service</Link> and <Link component={RouterLink} to="/privacy" target="_blank">Privacy Policy</Link>.</>} />
            </>
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {step === 1 && <Button onClick={() => setStep(0)}>Back</Button>}
            <Button type="submit" variant="contained" size="large" disabled={busy || (step === 1 && !form.accept_terms)}>
              {step === 0 ? "Continue" : "Create my workspace"}
            </Button>
          </Stack>
        </Stack>
      </form>
    </AuthLayout>
  );
}
